import { periodProgress } from '@obraplan/core/domain/reports/indicators';
import type { Project } from '@obraplan/core/domain/structure/project';
import { formatBusinessDate } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * O prazo da obra, como aparece na placa do canteiro: início, término e onde o hoje cai.
 *
 * É a mesma régua do painel, reduzida a uma obra inteira: a parte cheia é o que já
 * passou do prazo contratado, e o traço é o hoje **da obra**, pelo fuso dela (RN-123).
 * O percentual é de calendário, não de execução — quanto do prazo se foi, e não quanto
 * da obra está pronta. A frase ao lado diz isso com todas as letras.
 *
 * Sem as duas datas não há régua: uma barra vazia ou cheia diria algo que ninguém
 * cadastrou. Fica a frase que corresponde ao que se sabe.
 */
export function ProjectTerm({
  project,
  today,
  className,
}: {
  project: Project;
  today: string;
  className?: string;
}) {
  const { startDate, endDate } = project;

  if (startDate === undefined && endDate === undefined) {
    return (
      <p className={cn('text-xs text-(--color-content-muted)', className)}>Sem prazo cadastrado</p>
    );
  }

  if (startDate === undefined || endDate === undefined) {
    return (
      <p className={cn('text-xs text-(--color-content-muted)', className)}>
        {startDate !== undefined
          ? `${today < startDate ? 'Começa em' : 'Desde'} ${formatBusinessDate(startDate)} · sem término previsto`
          : `Término previsto em ${formatBusinessDate(endDate ?? '')} · sem início cadastrado`}
      </p>
    );
  }

  const progress = periodProgress(startDate, endDate, today);
  const pct = Math.round(progress.pct);
  const closed = project.status === 'FINISHED' || project.status === 'ARCHIVED';
  const caption =
    today < startDate
      ? `Começa em ${formatBusinessDate(startDate)}`
      : today > endDate
        ? `Prazo encerrado em ${formatBusinessDate(endDate)}`
        : `${String(pct)}% do prazo · termina em ${formatBusinessDate(endDate)}`;

  const description = `Prazo de ${formatBusinessDate(startDate)} a ${formatBusinessDate(endDate)}. ${caption}.`;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div
        role="img"
        aria-label={description}
        className="flex items-center gap-2 text-[11px] leading-none tabular text-(--color-content-muted)"
      >
        <span className="shrink-0">{formatBusinessDate(startDate)}</span>
        <span className="relative h-2 min-w-0 flex-1 overflow-visible rounded-sm bg-(--color-border-subtle)">
          <span
            className={cn(
              'absolute inset-y-0 left-0 rounded-sm',
              closed ? 'bg-(--color-state-neutral)' : 'bg-(--color-brand)',
            )}
            style={{ width: `${String(progress.pct)}%` }}
          />
          {today >= startDate && today <= endDate && (
            <span
              className="absolute -inset-y-1 w-0.5 -translate-x-1/2 bg-(--color-content)"
              style={{ left: `${String(progress.pct)}%` }}
            />
          )}
        </span>
        <span className="shrink-0">{formatBusinessDate(endDate)}</span>
      </div>
      <p className="text-xs text-(--color-content-muted)">{caption}</p>
    </div>
  );
}
